import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import Uppy from "@uppy/core";
import AwsS3 from "@uppy/aws-s3";
import axios from "axios";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { updateGameInStore } from "../redux/slice/game.slice";

const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL || "http://localhost:8000/api",
    withCredentials: true
});

const UploadQueueContext = createContext();

export const useUploadQueue = () => useContext(UploadQueueContext);

export function UploadQueueProvider({ children }) {
    const [uploads, setUploads] = useState([]);
    const activeUppyRef = useRef(null);
    const activeUploadIdRef = useRef(null);
    const dispatch = useDispatch();

    const uploadsRef = useRef([]);
    useEffect(() => {
        uploadsRef.current = uploads;
    }, [uploads]);

    // Helper to update a specific upload item's properties in the array
    const updateUploadItem = (id, updates) => {
        setUploads((prev) =>
            prev.map((item) => (item.id === id ? { ...item, ...updates } : item))
        );
    };

    // Main scheduler logic
    useEffect(() => {
        // Find if there is any item currently uploading
        const activeUpload = uploads.find((u) => u.status === "uploading");

        if (activeUpload) {
            // Already uploading something, do nothing
            return;
        }

        // Find the first queued item
        const nextQueueItem = uploads.find((u) => u.status === "queued");
        if (nextQueueItem) {
            startUploadTask(nextQueueItem);
        }
    }, [uploads]);

    const startUploadTask = async (item) => {
        const { id, gameId, platform, file } = item;

        // Mark as uploading in the state
        activeUploadIdRef.current = id;
        updateUploadItem(id, { status: "uploading", error: null });

        // Initialize Uppy for this specific upload
        const uppy = new Uppy({
            id: `uppy-queue-${id}`,
            autoProceed: false,
            restrictions: {
                maxNumberOfFiles: 1
            }
        });

        const fileIdRef = { current: null };

        uppy.use(AwsS3, {
            shouldUseMultipart: true,
            limit: 1,
            retryDelays: [0, 1000, 3000, 5000],
            allowedMetaFields: [],
            getChunkSize(f) {
                return 10 * 1024 * 1024; // 10MB chunk size
            },
            createMultipartUpload: async (f) => {
                try {
                    const response = await api.post("/upload/start", {
                        gameId,
                        platform,
                        fileName: f.name,
                        fileSize: f.size,
                        mimeType: f.type
                    });

                    if (response.data.isDuplicate) {
                        handleTaskSuccess(id, platform, gameId, response.data.url, f.size);
                        return { uploadId: "duplicate", key: "duplicate" };
                    }

                    fileIdRef.current = response.data.fileId;
                    updateUploadItem(id, { fileId: response.data.fileId });
                    return {
                        uploadId: response.data.uploadId,
                        key: response.data.key
                    };
                } catch (err) {
                    console.error("Start upload error:", err);
                    handleTaskError(id, err.message || "Failed to initialize upload session");
                    throw err;
                }
            },
            signPart: async (f, { uploadId, key, partNumber }) => {
                if (uploadId === "duplicate") {
                    return { url: "", headers: {} };
                }

                const controller = new AbortController();
                const timeout = setTimeout(() => {
                    controller.abort();
                }, 15000);

                try {
                    const response = await api.post(
                        "/upload/presigned-url",
                        {
                            uploadId,
                            key,
                            partNumber,
                            fileId: fileIdRef.current
                        },
                        { signal: controller.signal }
                    );

                    clearTimeout(timeout);
                    return {
                        url: response.data.url,
                        method: "PUT",
                        headers: {}
                    };
                } catch (err) {
                    clearTimeout(timeout);
                    console.error("signPart error:", err);
                    throw err;
                }
            },
            completeMultipartUpload: async (f, { uploadId, key, parts }) => {
                if (uploadId === "duplicate") {
                    return { location: "" };
                }
                try {
                    const response = await api.post("/upload/complete", {
                        uploadId,
                        key,
                        fileId: fileIdRef.current || `${gameId}-${platform}`,
                        parts: parts
                            .sort((a, b) => a.PartNumber - b.PartNumber)
                            .map((p) => ({
                                PartNumber: p.PartNumber,
                                ETag: p.ETag
                            }))
                    });

                    handleTaskSuccess(
                        id,
                        platform,
                        gameId,
                        response.data.url,
                        response.data.size,
                        response.data.key,
                        response.data.lowUrl,
                        response.data.lowKey
                    );
                    return { location: response.data.url };
                } catch (err) {
                    console.error("Complete upload error:", err);
                    handleTaskError(id, err.message || "Failed to finalize upload");
                    throw err;
                }
            },
            abortMultipartUpload: async (f, { uploadId, key }) => {
                if (uploadId === "duplicate") return;
                try {
                    await api.post("/upload/cancel", {
                        uploadId,
                        key,
                        fileId: fileIdRef.current || `${gameId}-${platform}`
                    });
                } catch (err) {
                    console.error("Abort upload error:", err);
                }
            },
            listParts: async (f, { uploadId, key }) => {
                if (uploadId === "duplicate") {
                    return [];
                }
                try {
                    const response = await api.post("/upload/resume", {
                        fileId: fileIdRef.current || `${gameId}-${platform}`
                    });
                    return (response.data.uploadedParts || []).map((p) => ({
                        PartNumber: p.partNumber,
                        ETag: p.etag,
                        Size: p.size || 0
                    }));
                } catch (err) {
                    console.error("List parts error:", err);
                    return [];
                }
            }
        });

        // Add file and trigger upload
        uppy.addFile({
            name: file.name,
            type: file.type,
            data: file
        });

        // Event bindings
        let lastProgressUpdate = 0;
        uppy.on("upload-progress", (f, progressInfo) => {
            const now = Date.now();
            const isComplete = progressInfo.bytesUploaded >= progressInfo.bytesTotal;
            if (!isComplete && now - lastProgressUpdate < 500) {
                return;
            }
            lastProgressUpdate = now;

            const progress = progressInfo.bytesUploaded ? Math.round((progressInfo.bytesUploaded / progressInfo.bytesTotal) * 100) : 0;
            let speed = 0;
            let eta = 0;
            if (progressInfo.bytesUploaded) {
                speed = progressInfo.bytesUploaded / ((now - uppy.startedTime) / 1000) || 0;
                const bytesLeft = progressInfo.bytesTotal - progressInfo.bytesUploaded;
                eta = speed > 0 ? Math.round(bytesLeft / speed) : 0;
            }

            updateUploadItem(id, { progress, speed, eta });
        });

        uppy.on("error", (err) => {
            console.error("Uppy queue task error:", err);
            handleTaskError(id, err.message || "An upload error occurred");
        });

        uppy.on("upload-error", (f, error) => {
            console.error("Uppy queue task upload-error:", error);
            handleTaskError(id, error?.message || "Chunk upload failed");
        });

        activeUppyRef.current = uppy;
        uppy.startedTime = Date.now();
        uppy.upload();
    };

    const handleTaskSuccess = async (id, platform, gameId, url, size, key, lowUrl, lowKey) => {
        updateUploadItem(id, { status: "completed", progress: 100 });
        cleanupActiveUppy(id);

        try {
            const response = await api.patch(`/games/${gameId}/platform-file`, {
                platform,
                download_link: url,
                size,
                public_id: key,
                lowUrl: lowUrl,
                lowPublic_id: lowKey
            });
            const niceName = ['cover_image', 'video', 'gallery'].includes(platform)
                ? platform.replace('_', ' ')
                : platform.toUpperCase() + ' binary';
            toast.success(`${niceName.charAt(0).toUpperCase() + niceName.slice(1)} uploaded & linked successfully!`);

            // Check if this was the last file in the queue for this gameId
            const otherPending = uploadsRef.current.filter(u => u.gameId === gameId && u.id !== id && u.status !== 'completed');
            if (otherPending.length === 0 && response.data?.game) {
                dispatch(updateGameInStore(response.data.game));
            }
        } catch (err) {
            console.error("Failed to register game platform link:", err);
            const niceName = ['cover_image', 'video', 'gallery'].includes(platform)
                ? platform.replace('_', ' ')
                : platform;
            toast.error(`Failed to register ${niceName} to game database`);
        }

        // Clean up completed item from UI history after 8 seconds
        setTimeout(() => {
            setUploads((prev) => prev.filter((item) => item.id !== id));
        }, 8000);
    };

    const handleTaskError = (id, errorMsg) => {
        updateUploadItem(id, { status: "error", error: errorMsg });
        cleanupActiveUppy(id);
    };

    const cleanupActiveUppy = (id) => {
        if (activeUploadIdRef.current === id) {
            if (activeUppyRef.current) {
                try {
                    activeUppyRef.current.destroy();
                } catch (e) {
                    console.error("Error destroying uppy", e);
                }
                activeUppyRef.current = null;
            }
            activeUploadIdRef.current = null;
        }
    };

    // Public API Actions
    const addToQueue = (gameId, gameTitle, platform, file, silent = false) => {
        const id = platform === 'gallery' ? `${gameId}-${platform}-${file.name}` : `${gameId}-${platform}`;
        const newUpload = {
            id,
            gameId,
            gameTitle,
            platform,
            file,
            status: "queued",
            progress: 0,
            speed: 0,
            eta: 0,
            error: null,
            selectedFile: {
                name: file.name,
                size: file.size,
                type: file.type
            }
        };

        setUploads((prev) => {
            // If already exists and is in progress/queued/paused, cancel it first
            const existing = prev.find((u) => u.id === id);
            if (existing) {
                cancelUpload(id);
                return [...prev.filter((u) => u.id !== id), newUpload];
            }
            return [...prev, newUpload];
        });

        if (!silent) {
            const niceName = ['cover_image', 'video', 'gallery'].includes(platform)
                ? platform.replace('_', ' ')
                : platform.toUpperCase() + ' binary';
            toast.success(`Added ${niceName} to upload queue`);
        }
    };

    const pauseUpload = (id) => {
        if (activeUploadIdRef.current === id && activeUppyRef.current) {
            activeUppyRef.current.pauseAll();
            updateUploadItem(id, { status: "paused" });
        }
    };

    const resumeUpload = (id) => {
        // Check if this was the active one and was paused
        const item = uploads.find((u) => u.id === id);
        if (item && item.status === "paused") {
            if (activeUploadIdRef.current === id && activeUppyRef.current) {
                activeUppyRef.current.resumeAll();
                updateUploadItem(id, { status: "uploading" });
            } else {
                // Not current active uppy, queue it again
                updateUploadItem(id, { status: "queued" });
            }
        }
    };

    const cancelUpload = (id) => {
        if (activeUploadIdRef.current === id && activeUppyRef.current) {
            activeUppyRef.current.cancelAll();
            cleanupActiveUppy(id);
        }
        setUploads((prev) => prev.filter((item) => item.id !== id));
    };

    const retryUpload = (id) => {
        updateUploadItem(id, { status: "queued", progress: 0, error: null });
    };

    return (
        <UploadQueueContext.Provider
            value={{
                uploads,
                addToQueue,
                pauseUpload,
                resumeUpload,
                cancelUpload,
                retryUpload
            }}
        >
            {children}
        </UploadQueueContext.Provider>
    );
}
